import React, {useEffect, useState} from "react";
import {useTagContext} from "../../contexts/tag_context";
import {useGroupContext} from "../../contexts/group_context";
import {useUserContext} from "../../contexts/user_context";
import {useJobContext} from "../../contexts/job_context";
import {useNavigationContext} from "../../contexts/NavContext/nav-context";
import {useNotificationContext} from "../../contexts/notification_context";
import {JobRead, TagCreate, TagRead, TagType} from "../../schemas";
import {requiresAssociationConsistency} from "../../schemas/tag";
import {withApiErrorHandlerThrow} from "../../error_handling";
import Swal from "sweetalert2";
import {ThemeColors} from "../../colors";
import AsyncSelect from "react-select/async";
import {OnChangeValue} from "react-select/dist/declarations/src/types";
import Button from "../../components/SharedComponents/Button/Button";
import {TextSizes} from "../../sizes";
import Popup from "../../components/SharedComponents/Popup";
import {FiEdit2, FiPlus} from "react-icons/fi";
import {createTags, queueTagInference, deleteTag} from "../../api";

interface SelectTagOption {
    value: string;
    label: string;
    id?: string;
}

const UpdateWorkspaceCodesPopup: React.FC<{ close: () => void }> = ({close}) => {
    const {tagMap, reFetchTags} = useTagContext()!;
    const {currentGroup} = useGroupContext()!;
    const {currentUser} = useUserContext()!;
    const {addJobs} = useJobContext()!;
    const {navigateTo, JobsBlocker} = useNavigationContext()!;
    const {openJobsPopup} = useNotificationContext()!;

    const [loading, setLoading] = useState(false);
    const [initialPositiveTags, setInitialPositiveTags] = useState<SelectTagOption[]>([]);
    const [suggestedPositiveTags, setSuggestedPositiveTags] = useState<SelectTagOption[]>([]);

    // Check if there are unsaved changes
    const hasChanges = () => {
        const startSet = new Set(initialPositiveTags.map(t => t.value));
        const currentSet = new Set(suggestedPositiveTags.map(t => t.value));
        if (startSet.size !== currentSet.size) return true;
        for (const val of startSet) {
            if (!currentSet.has(val)) return true;
        }
        return false;
    };

    // Reset to initial state
    const handleReset = () => {
        setSuggestedPositiveTags([...initialPositiveTags]);
    };



    useEffect(() => {
        const init = Array.from(tagMap.values())
            .filter((tag) => requiresAssociationConsistency(tag) && tag.type !== TagType.REQUEST_SET)
            .map(tag => ({
                value: tag.tag_name.toLowerCase(),
                label: tag.tag_name,
                id: tag.id
            }));
        setSuggestedPositiveTags(init);
        setInitialPositiveTags(init);
    }, [tagMap]);

    const handleDataSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();

        const updateTags = async () => {
            setLoading(true);
            try {
                const startPos = new Set(initialPositiveTags.map((opt) => opt.value));
                const endPos = new Set(suggestedPositiveTags.map((opt) => opt.value));

                const addedPos: SelectTagOption[] =
                    suggestedPositiveTags.filter(tagOption => !startPos.has(tagOption.value));
                const removedPos: SelectTagOption[] =
                    initialPositiveTags.filter(tagOption => !endPos.has(tagOption.value));

                const newTagIds: string[] = [];
                for (const tag of addedPos) {
                    const tagData: TagCreate = {
                        tag_name: tag.value,
                        description: tag.value,
                        type: TagType.TAG,
                        group_id: currentGroup!.id,
                        submitter_id: currentUser!.id,
                    };
                    const newTag = (await withApiErrorHandlerThrow<TagRead[]>(
                        () => createTags([tagData]),
                        {titleQualifier: "Error Creating Tag", navigateTo: navigateTo}
                    ))[0];

                    if (newTag) {
                        newTagIds.push(newTag.id!);
                    }
                }
                if (newTagIds.length > 0) {
                    const newJob: JobRead = await withApiErrorHandlerThrow(
                        () => queueTagInference(newTagIds),
                        {titleQualifier: "Error Queuing Tag Inference", navigateTo: navigateTo}
                    );
                    addJobs([newJob]);
                    openJobsPopup();
                }

                for (const tag of removedPos) {
                    await withApiErrorHandlerThrow(
                        () => deleteTag(tag.id!),
                        {titleQualifier: "Error Deleting Tag", navigateTo: navigateTo}
                    );
                }

                await reFetchTags();
            } finally {
                setLoading(false);
            }
        };

        const result = await Swal.fire({
            title: "Are you sure?",
            text: "You are about to update the workspace tags.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: ThemeColors.neutralDarkHex,
            cancelButtonColor: ThemeColors.colorContrastLightHex,
            confirmButtonText: "Yes, update it!",
        });

        if (result.isConfirmed) {
            await updateTags();
            close();
        }
    };

    function promiseOptions(inputValue: string): Promise<SelectTagOption[]> {
        return new Promise<SelectTagOption[]>(resolve => {
            setTimeout(() => {
                const options: SelectTagOption[] = [];
                if (
                    inputValue.length > 0 &&
                    options.every((option) => option.label.toLowerCase() !== inputValue.toLowerCase())
                ) {
                    options.push({
                        value: inputValue.toLowerCase(),
                        label: "Create new tag: " + inputValue,
                    });
                }
                resolve(options);
            }, 100);
        });
    }

    return (
        <JobsBlocker onUploadJobs>
            <div
                className={"w-full rounded p-5 shadow-lg" + ThemeColors.colorLight}
                style={{maxHeight: 'calc(100vh - 2rem)'}}
            >
                <form onSubmit={handleDataSubmit}>
                    <AsyncSelect
                        className="mt-2 mb-2"
                        isMulti
                        isClearable
                        noOptionsMessage={() => "Create New Code By Typing..."}
                        loadOptions={promiseOptions}
                        cacheOptions
                        value={suggestedPositiveTags}
                        defaultValue={initialPositiveTags}
                        onChange={(newValue: OnChangeValue<SelectTagOption, true>) => {
                            // Directly use newValue - handles all actions (select, remove, clear, pop)
                            setSuggestedPositiveTags([...(newValue as SelectTagOption[])]);
                        }}
                        placeholder="Select tags that apply..."
                    />

                    {/* Action buttons */}
                    <div className="flex justify-between">
                        <Button
                            type="button"
                            size={"wide"}
                            color="light"
                            additionalClassName={TextSizes.small}
                            onClick={handleReset}
                            disabled={!hasChanges() || loading}
                            title="Reset Changes"
                        />
                        <Button
                            size={"wide"}
                            additionalClassName={TextSizes.small}
                            loading={loading}
                            title={hasChanges() ? "Update Codes" : "No Changes"}
                            disabled={!hasChanges()}
                        />
                    </div>
                </form>
            </div>
        </JobsBlocker>
    );
};

export const ManageGroupTags: React.FC = () => {
    const [formPopup, setFormPopup] = React.useState(false);
    const {codeTags} = useTagContext()!;

    return (
        <>
            {formPopup && (
                <div>
                    <Popup
                        isOpen={formPopup}
                        title={"Update Codes For Workspace"}
                        close={() => setFormPopup(false)}
                        closeText="Cancel & Exit"
                    >
                        <UpdateWorkspaceCodesPopup close={() => setFormPopup(false)}/>
                    </Popup>
                </div>
            )}

            {/* Icon Button */}
            <div className="flex justify-end m-2">
                <button
                    type="button"
                    className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-[#a1a1b6] hover:border-[#8c8ca3] transition-colors cursor-pointer text-sm text-[#1c1c24]"
                    onClick={() => setFormPopup(true)}
                >
                    {codeTags.length > 0
                        ? <FiEdit2 className="text-[#6d6d8a] w-[1em] h-[1em]"/>
                        : <FiPlus className="text-[#6d6d8a] w-[1em] h-[1em]"/>}
                    <span className="font-medium">{
                        codeTags.length > 0 ? "Modify Issue Codes" : "Create Issue Codes"
                    }</span>
                </button>
            </div>
        </>
    );
};
